/**
 * oauthMessages — the postMessage contract between an OAuth popup window and
 * the opener that launched it.
 *
 * When the popup-based social sign-in flow completes, the popup lands on
 * /auth/oauth/complete (success) or /login?oauth_error=<reason> (failure).
 * Those pages detect they are running inside a popup (window.opener) and relay
 * the result back to the opener via window.opener.postMessage(...), then close
 * themselves. The opener (useOAuthSignIn) listens for these messages.
 *
 * SECURITY: every postMessage uses targetOrigin === window.location.origin
 * (never '*'), and every receiver validates event.origin === origin AND the
 * `type` tag before acting on the payload. Same-origin only — the popup and
 * opener are both served from app.gamedevbuddy.com.
 */

/** Success message: the popup obtained a JWT and relays it to the opener. */
export const OAUTH_TOKEN_MESSAGE = 'gdb-oauth-token' as const
/** Failure message: the popup hit an oauth_error and relays the reason code. */
export const OAUTH_ERROR_MESSAGE = 'gdb-oauth-error' as const

export interface OAuthTokenMessage {
  type: typeof OAUTH_TOKEN_MESSAGE
  token: string
}

export interface OAuthErrorMessage {
  type: typeof OAUTH_ERROR_MESSAGE
  reason: string
}

export type OAuthPopupMessage = OAuthTokenMessage | OAuthErrorMessage

/**
 * Narrow an unknown postMessage payload to a typed OAuth popup message.
 * Returns null for anything that isn't a well-formed token/error message —
 * callers must also have already validated event.origin.
 */
export function parseOAuthMessage(data: unknown): OAuthPopupMessage | null {
  if (typeof data !== 'object' || data === null) return null
  const obj = data as Record<string, unknown>

  if (obj.type === OAUTH_TOKEN_MESSAGE && typeof obj.token === 'string' && obj.token.length > 0) {
    return { type: OAUTH_TOKEN_MESSAGE, token: obj.token }
  }
  if (obj.type === OAUTH_ERROR_MESSAGE && typeof obj.reason === 'string') {
    return { type: OAUTH_ERROR_MESSAGE, reason: obj.reason }
  }
  return null
}
